import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfilePublicRelayDto } from '@diritto/common/relay/dtos/profile-relay.dtos';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackerExtraInfo, TrackerPosition } from '@diritto/common/tracker/tracking-event.constant';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { DotDividerIcon } from 'icons/DotDividerIcon';
import { ContentFavoriteAndEpisodeNotiButton } from 'app-components/contents/ContentFavoriteAndEpisodeNotiButton';
import { ProfileName } from 'app-components/contents/ProfileName';
import React, { MouseEvent } from 'react';
import { getTextLineStyle } from 'styles/common';
import { Link } from 'ui-components/common/Link';
import { ContentCover } from 'ui-components/contents/ContentCover';

interface ContentItemMetaProps {
  className?: string;
  content: ContentPublicRelayDto;
  profile?: ProfilePublicRelayDto;
  color: string;
}

function stopPropagation(e: MouseEvent) {
  e.stopPropagation();
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Viewer,
};

export const FancyShareContentItemMeta = ({ className, content, profile, color }: ContentItemMetaProps) => {
  const {
    title,
    counts: { episodeCount },
  } = content;

  return (
    <StyledMetaWrapper color={color} className={className}>
      <Link href={`/contents/${content.id}`}>
        <ContentCover content={content} size={60} showBadge={false} />
      </Link>
      <StyledContentItemMeta>
        <Link href={`/contents/${content.id}`}>
          <StyledTitle color={color}>{title}</StyledTitle>
        </Link>
        <StyledAuthor color={color}>
          <StyledNickname profile={profile} />
          {!!episodeCount && (
            <>
              <DotDividerIcon color={color} opacity={0.2} />
              <StyledEpisodeCount>
                <p>{`${episodeCount}화`}</p>
              </StyledEpisodeCount>
            </>
          )}
        </StyledAuthor>
      </StyledContentItemMeta>
      <StyledIcon onClick={stopPropagation}>
        <ContentFavoriteAndEpisodeNotiButton
          key="favoriteAndEpisodeNoti"
          contentId={content.id}
          color={color}
          opacity={0.5}
          trackerExtraInfo={trackerExtraInfo}
        />
      </StyledIcon>
    </StyledMetaWrapper>
  );
};

const StyledMetaWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledTitle = styled.p(
  ({ color }: { color: string }) => css`
    font-weight: ${FontWeight.Bold};
    ${Typography.XSmall};
    ${getTextLineStyle(2)};
    color: ${color};
  `,
);

const StyledAuthor = styled.div(
  ({ color }: { color: string }) => css`
    display: flex;
    color: ${color};
    align-items: center;
    margin-top: 4px;

    & a {
      flex-basis: fit-content;
      ${Typography.XXXSmall};
      ${getTextLineStyle(1)};
    }

    & p {
      flex-basis: fit-content;
      ${Typography.XXXSmall};
      ${getTextLineStyle(1)};
    }

    & svg {
      margin: 0 4px;
    }
  `,
);

const StyledIcon = styled.div`
  display: flex;
  margin-left: auto;
  flex-direction: row;
  align-items: center;
  gap: 18px;
`;

const StyledContentItemMeta = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledNickname = styled(ProfileName)`
  opacity: 0.6;
`;

const StyledEpisodeCount = styled.div`
  opacity: 0.6;
`;
